/**
 * Item detail — evidence, fields, and the history timeline (spec §3.1, §4.1).
 *
 * Renders `GET /api/items/:itemId`, i.e. `getItemDetail`: the SAME projected
 * row the Item list shows (not re-projected — a detail page that disagreed
 * with the list it was opened from about status or stall would be worse than
 * one that showed less), its presigned evidence, and its timeline off the
 * append-only `item_events` log.
 *
 * ## Two blocks the source design draws are deliberately NOT here
 *
 * **No audio player, no waveform, no clip, no duration** — anywhere, in any
 * form. Spec §7.3: walk audio is diarization input, never a user-facing
 * artifact. `getItemDetail` enforces this structurally (its evidence
 * allowlist is `image` / `video`, so an audio media row is dropped before it
 * is even presigned, and `ItemEvidence` has no field that could carry a
 * duration). The design's fallback line — "Audio wasn't retained for this
 * walk" — is absent for the same reason: there is no audio to have retained,
 * so saying so would imply a feature that does not exist.
 *
 * **No prior-occurrences ("Seen before") block.** Cut in spec §3.3: it needs
 * cross-item similarity clustering, which is a project on its own.
 *
 * ## Editing follows the mobile schema
 *
 * Accepted, mobile-schema-backed records can be edited here.  The browser
 * never writes a model itself: it posts the exact field patch to the
 * authenticated backend route, which invokes the same `JobSiteManager`
 * operation used by the mobile item's Edit sheet.
 */

import { useEffect, useRef, useState } from "react";
import {
  deleteMutation,
  fetchItemDetail,
  fetchItemEditOptions,
  postItemEdit,
  postItemRetrySync,
  postMutation,
  updateWalkPhotoDescription,
  updateWalkPhotoLocation,
  type MutationResult,
  type ApiError,
  type ItemEditDirectoryOption,
  type ItemEditOptions,
  type ItemDetailResponse,
  type ItemHistoryRow,
  type ConsoleRow,
} from "../api";
import { useResource } from "../useResource";
import { resourceQuery } from "../resourceCache";
import { C, FONT_MONO } from "../tokens";
import { ITEM_DETAIL_COLUMNS } from "../layout";
import {
  Card,
  ConsoleViewProps,
  EvidenceTile,
  Eyebrow,
  FailurePanel,
  KIND_LABEL,
  KindChip,
  LoadingPanel,
  META,
  Pill,
  QuietLine,
  SEVERITY,
  STATUS_CHIP,
  StaleBanner,
  ViewBody,
  clockOf,
  humanize,
  plural,
  whenStamp,
} from "../components/chrome";
import { NudgeSheet } from "../components/NudgeSheet";
import { RecordPhotoPicker } from "../components/RecordPhotoPicker";
import { readRecordPhoto } from "../lib/recordPhoto";
import { PhotoFullscreenViewer, type FullscreenPhoto } from "../components/PhotoFullscreenViewer";
import { FieldRows } from "./ReviewQueue";
// The Brief's day count, not a copy of it: one spelling of "how many whole
// days old is this row" for both pages.
import { wholeDaysSince } from "./Brief";
import { reviewRecordHref } from "../lib/reviewHref";
import { PropertyEditor } from "../components/PropertyRows";
import { copyToClipboard } from "../lib/clipboard";
import { TimeField } from "../components/TimeField";
import { RecordDetail, SYNC_TONE, SyncRow, recordSyncLook, syncActivityRow, syncNoun, type RecordActivityRow, type RetrySync, type SyncLook, type SyncProvider } from "./RecordDetail";
import { usesRecordLayout } from "./recordDetailFields";
import {
  mobileRecordDefinition,
  mobileRecordFields,
  mobileRecordKindLabel,
} from "../mobileRecordPresentation";
import {
  DAILY_LOG_STATUS_OPTIONS,
  DEFAULT_PROCORE_OBSERVATION_TYPE_OPTIONS,
  OBSERVATION_PRIORITY_OPTIONS,
  OBSERVATION_STATUS_OPTIONS,
  PROCORE_DELAY_TYPE_OPTIONS,
  type ProcoreDailyLogFieldDefinition,
  type ProcoreDailyLogFieldValue,
} from "../../../../backend/src/shared/procoreDailyLog";

// ---------------------------------------------------------------------------
// The timeline
// ---------------------------------------------------------------------------

/** The person who acted, or `undefined` for a system event.
 *
 *  ONLY a name. The raw id used to be the fallback here, on the theory that
 *  it beat an anonymous line; it does not — `backend` stores `{ userId }`
 *  with no `displayName` on every event it writes, so that fallback was not
 *  an edge case, it was the timeline, and every row read
 *  "user_3EN3DD… reviewed it". `getItemDetail` now resolves the name
 *  server-side (Mongo mirror → org seats → Clerk); when no source can name
 *  the actor, the event's unnamed sentence ("Reviewed") is the honest
 *  answer. Never print an opaque id where a person belongs. */
function actorName(entry: ItemHistoryRow): string | undefined {
  const actor = entry.actor;
  if (!actor || "system" in actor) return undefined;
  return actor.displayName || undefined;
}

/**
 * What an edited FIELD KEY is called on screen.
 *
 * Two sources, in this order, and no third:
 *
 *  1. this table — the record-level keys the four edit paths share. They are
 *     model column names (`summary`, `tradeText`, `assigneeText`), which are
 *     not what a user calls them, so they are named once, here.
 *  2. the mobile schema for the record's own category
 *     (`PROCORE_DAILY_LOG_CATEGORY_DEFINITIONS`) — the SAME definitions the
 *     record editor renders its labels from, so a field reads identically in
 *     the timeline and on the form above it. Daily-log edits land in
 *     `fieldValues`, whose keys are schema-defined and per-category.
 *
 * A key neither source knows is DROPPED, never printed raw. This is the same
 * rule the actor name follows: an internal identifier on screen is worse than
 * a shorter sentence. Dropping everything leaves "edited it", which is true.
 *
 * Labels are BARE ("due date", not "the due date"): the article belongs to
 * the one-field sentence, and a list of them reads as a list — "updated
 * priority, due date, and category", not "updated the priority, the due
 * date, and the category".
 */
const EDIT_FIELD_LABELS: Record<string, string> = {
  title: "title",
  content: "description",
  description: "description",
  summary: "description",
  details: "details",
  priority: "priority",
  status: "status",
  workflowStatus: "status",
  assignee: "assignee",
  assigneeText: "assignee",
  dueDate: "due date",
  taskCategory: "category",
  category: "category",
  trade: "trade",
  tradeText: "trade",
  vendor: "company",
  company: "company",
  location: "location",
  type: "type",
};

/** The field labels for one edit, in the order the writer diffed them. */
function editedFieldLabels(
  entry: ItemHistoryRow,
  item: Pick<ConsoleRow, "recordType" | "category">,
): string[] {
  const raw = entry.payload?.fields;
  if (!Array.isArray(raw)) return [];
  const schema = mobileRecordDefinition(item);
  const labels: string[] = [];
  for (const key of raw) {
    if (typeof key !== "string") continue;
    const schemaLabel = schema?.fields.find((field) => field.key === key)?.label;
    const label = EDIT_FIELD_LABELS[key] ?? (schemaLabel ? schemaLabel.toLowerCase() : undefined);
    if (label && !labels.includes(label)) labels.push(label);
  }
  return labels;
}

/** "a", "a and b", "a, b, and c" — the Oxford comma is the design's. */
function joinLabels(labels: string[]): string {
  if (labels.length <= 1) return labels[0] ?? "";
  if (labels.length === 2) return `${labels[0]} and ${labels[1]}`;
  return `${labels.slice(0, -1).join(", ")}, and ${labels[labels.length - 1]}`;
}

/**
 * One timeline entry, as a sentence.
 *
 * The two assignment events read as assignments and NAME NOBODY. The log's
 * `ballHolder` column is the same free-text denormalized assignee the console
 * stopped displaying everywhere else (spec §4); printing it here would be the
 * ball affordance surviving in the one place nobody thought to look. That the
 * assignment HAPPENED is real history and stays.
 *
 * REVIEW vs EDIT. `reviewed` is the first look after the walk — the
 * accept/reject pass. `edited` is someone changing a value on a record that
 * already exists. They were one event type until 2026-09-20, so a record
 * accepted once and corrected twice read as three identical "reviewed it"
 * lines; the split is at the writer (`itemMovement.ts`), because a read-only
 * console cannot recover the difference. Rows written BEFORE the split are
 * all `reviewed` and still read that way — history is not rewritten.
 *
 * `payload` is passed through by `getItemDetail` verbatim and is `Mixed` in
 * the schema, so only `status` (a string) and `fields` (a string array) are
 * read here, each checked for its own shape.
 */
/** How close a logged `synced` event and the record's `syncedAt` must be to
 *  count as one sync: the event is written beside the field, not with it. */
const SYNC_SAME_MS = 60_000;

function syncEventProvider(entry: ItemHistoryRow, item: Pick<ConsoleRow, "syncSource">): SyncProvider {
  const provider = entry.payload?.provider;
  if (provider === "procore" || provider === "autodesk") return provider;
  // Written before the payload named one: the record's own system.
  return item.syncSource === "autodesk" ? "autodesk" : "procore";
}

/** A logged push (`syncHistory.ts`): the Procore task number on creation,
 *  "Update synced to Procore" on a re-sync, "ACC sync failed · why". */
function syncEventLine(
  entry: ItemHistoryRow,
  item: Pick<ConsoleRow, "recordType" | "category" | "syncSource"> & Partial<Pick<ConsoleRow, "kind">>,
): { sentence: string; detail?: string; sync: "ok" | "bad" } {
  const provider = syncEventProvider(entry, item);
  const name = provider === "autodesk" ? "ACC" : "Procore";
  if (entry.type === "sync_failed") {
    const error = typeof entry.payload?.error === "string" ? entry.payload.error : undefined;
    return { sentence: `${name} sync failed`, ...(error ? { detail: error } : {}), sync: "bad" };
  }
  const number = entry.payload?.remoteNumber;
  const ref = (typeof number === "string" || typeof number === "number") && String(number)
    ? `${syncNoun(item as ConsoleRow, provider)} #${number}`
    : undefined;
  return {
    sentence: entry.payload?.mode === "updated" ? `Update synced to ${name}` : `Synced to ${name}`,
    ...(ref ? { detail: ref } : {}),
    sync: "ok",
  };
}

/** One line of the record's activity, newest first. `entry` is the logged
 *  event behind it; the record's current sync state, when no logged push is
 *  that same sync, is a line with no entry. */
type ActivityLine = RecordActivityRow & { entry?: ItemHistoryRow };

/**
 * The history, newest first, with the record's sync state slotted in by time.
 *
 * Every push now logs a line (`synced` / `sync_failed`), so the state usually
 * IS the newest of them and is folded into it rather than said twice: a
 * success when the newest push of that system is a `synced` at the same
 * instant, a failure when it is a `sync_failed` (a repeated failure is logged
 * once, so its instant can be older than the record's last attempt). A state
 * with no logged twin — a queued push, or one from before pushes were logged —
 * goes in by its instant, or leads the list when it has none.
 */
export function buildActivity(history: ItemHistoryRow[], item: ConsoleRow, look: SyncLook | null): ActivityLine[] {
  const newest = [...history].reverse();
  const lines: ActivityLine[] = newest.map((entry) => {
    const sync = entry.type === "synced" || entry.type === "sync_failed" ? syncEventLine(entry, item) : null;
    return {
      id: entry.id,
      entry,
      sentence: sync ? sync.sentence : historySentence(entry, item),
      when: whenStamp(entry.at),
      actor: entry.actor && !("system" in entry.actor) ? actorName(entry) : undefined,
      ...(sync
        ? { sync: sync.sync, ...(sync.detail ? { detail: sync.detail } : {}) }
        // A board move's comment ("What was done?") is the line under it.
        : typeof entry.payload?.note === "string" && entry.payload.note ? { detail: `“${entry.payload.note}”` } : {}),
    };
  });
  const current = syncActivityRow(look);
  if (!current || !look) return lines;
  const { at, ...row } = current;
  const latest = newest.findIndex((entry) =>
    (entry.type === "synced" || entry.type === "sync_failed") && syncEventProvider(entry, item) === look.provider);
  const twin = latest === -1 ? undefined : newest[latest]!;
  const same = twin && (
    (look.tone === "ok" && twin.type === "synced" && (!at || Math.abs(Date.parse(twin.at) - Date.parse(at)) < SYNC_SAME_MS))
    || (look.tone === "bad" && twin.type === "sync_failed"));
  if (same) {
    const line = lines[latest]!;
    // The record's words win for a failure (its latest reason); a success
    // keeps the logged number and borrows the record's when it has none.
    lines[latest] = { ...line, detail: look.tone === "bad" ? row.detail ?? line.detail : line.detail ?? row.detail };
    return lines;
  }
  const index = at ? newest.findIndex((entry) => Date.parse(entry.at) < Date.parse(at)) : 0;
  lines.splice(index === -1 ? lines.length : index, 0, row);
  return lines;
}

export function historySentence(
  entry: ItemHistoryRow,
  /** The record itself, for the per-category field labels. Optional so the
   *  sentence still forms without it — every edit then reads "edited it". */
  item?: Pick<ConsoleRow, "recordType" | "category" | "syncSource">,
): string {
  const who = actorName(entry);
  const status =
    typeof entry.payload?.status === "string"
      ? entry.payload.status
      : undefined;

  switch (entry.type) {
    case "drafted":
      return who ? `Drafted from ${who}'s walk` : "Drafted from the walk";
    case "reviewed":
      return who ? `${who} reviewed it` : "Reviewed";
    case "edited": {
      const labels = item ? editedFieldLabels(entry, item) : [];
      if (labels.length === 0) return who ? `${who} edited it` : "Edited";
      // One field reads as an edit OF that field; several read as an update
      // ACROSS them, which is how the design's own line is worded.
      const what = joinLabels(labels);
      if (labels.length === 1) {
        return who ? `${who} edited the ${what}` : `Edited the ${what}`;
      }
      return who ? `${who} updated ${what}` : `Updated ${what}`;
    }
    case "ball_assigned":
      return who ? `${who} assigned it` : "Assigned";
    case "ball_changed":
      return who ? `${who} reassigned it` : "Reassigned";
    case "status_changed":
      return status
        ? `Status changed to ${humanize(status)}`
        : "Status changed";
    case "nudged":
      return who ? `${who} prepared a follow-up` : "A follow-up was prepared";
    case "media_added":
      return who ? `${who} added a photo` : "A photo was added";
    case "closed":
      return who ? `${who} closed it` : "Closed";
    case "synced":
    case "sync_failed":
      return item ? syncEventLine(entry, item).sentence : entry.type === "synced" ? "Synced" : "Sync failed";
    case "reprocessed":
      return "Reprocessed from the walk";
    case "recaptioned":
      return "Photo captions regenerated";
    case "translated":
      return "Translated";
    default:
      return humanize(entry.type);
  }
}

/**
 * The dot's colour, following the design's own key: gold for something the
 * system did, ink for a named person, green for a completion. Red is reserved
 * for the synthesized stall row at the end of the list.
 */
function historyDot(entry: ItemHistoryRow): string {
  if (entry.type === "closed") return C.green;
  // A PERSON, not a name: an actor nobody could name is still not the
  // machinery, and colouring it gold would say the system did it.
  const byPerson = !!entry.actor && !("system" in entry.actor);
  return byPerson ? C.ink : C.gold;
}

/** The mono line under the sentence. The design carries a provenance clause
 *  there ("AUTO FROM SPEAKER, CONFIRMED"); the log does not record provenance,
 *  so this prints only what is actually known: when, whether a machine did it,
 *  and
 *  whether it reset the stall clock. */
function historyWhen(entry: ItemHistoryRow): string {
  const parts = [whenStamp(entry.at)];
  if (entry.actor && "system" in entry.actor) parts.push("automatic");
  if (!entry.isMovement) parts.push("not movement");
  return parts.join(" · ");
}

/**
 * 17a's header chip: mono 9, 0.09em, uppercase, on 5% ink.
 *
 * `priority` swaps the ground for gold at 28% and the ink a shade darker —
 * the design's one tinted chip, because urgency is the one of the three that
 * is a judgement rather than a fact about the record's type or state.
 *
 * Its own component rather than `Pill`, which carries a per-status colour
 * ramp: three differently-coloured chips in a row is a traffic light, and
 * the header is not where this page raises alarms (12a puts the overdue
 * treatment on the due date itself).
 */
function HeaderChip({ label, tone }: { label: string; tone?: "priority" }) {
  return (
    <span
      data-testid="header-chip"
      style={{
        ...META,
        fontSize: 10.35,
        letterSpacing: "0.09em",
        color: tone === "priority" ? C.ink : C.sub,
        padding: "4px 8px",
        borderRadius: 5,
        background: tone === "priority" ? C.goldChip : C.hairRow,
        whiteSpace: "nowrap",
      }}
    >
      {label}
    </span>
  );
}

function TimelineEntry({
  what,
  when,
  dot,
  testId,
}: {
  what: string;
  when: string;
  dot: string;
  testId: string;
}) {
  /*
   * 17a's entry: an 8px dot and two lines, and NO connecting rail.
   *
   * The rail was drawn on the argument that a timeline continues rather than
   * stops. It also spent 22px of a column and a vertical line on every row of
   * a list whose entries are already in order and already dated — and in the
   * left column, beside the property table, that line read as a second table
   * edge. The dot carries the colour, which is the only thing the rail was
   * adding to.
   */
  return (
    <div data-testid={testId} style={{ display: "flex", gap: 12 }}>
      <span
        style={{
          width: 8,
          height: 8,
          borderRadius: 4,
          background: dot,
          marginTop: 5,
          flex: "none",
        }}
      />
      <span style={{ flex: 1, minWidth: 0, display: "flex", flexDirection: "column", gap: 1 }}>
        <span style={{ fontSize: 15.52, fontWeight: 500, lineHeight: 1.35 }}>{what}</span>
        <span style={{ fontFamily: FONT_MONO, fontSize: 11.5, color: C.sub2 }}>{when}</span>
      </span>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Fields
// ---------------------------------------------------------------------------

/**
 * One row of the Record card, to the design ("Led AI - Item Record Card").
 *
 * The row is the unit, not the label-and-value pair: 60px tall, full-bleed to
 * the card's edges with its own 22px inset, a 150px label column, and the
 * value set as a VALUE — 17px, heavy, right-aligned — rather than as small
 * grey metadata. A record's fields are the record; the design stopped
 * printing them at footnote size, and this stops too.
 *
 * ## Copying is the row, not a button on it
 *
 * Every one of these values gets retyped into Procore by somebody, and a
 * value you can only copy by dragging a mouse across it gets retyped wrong.
 * So the whole row is the target: it tints under the pointer, a copy mark
 * fades in beside the label, and clicking anywhere on the row takes the
 * value. The mark is a real `<button>` as well — it is the keyboard's way in,
 * and it is what assistive tech announces — but a mouse never has to find it.
 *
 * On success the mark becomes a tick and the row holds a green tint for a
 * moment. That pause is the point: a copy that reported nothing is a copy the
 * reader has to verify by pasting somewhere to find out.
 */
/**
 * How long the page waits after the last keystroke before saving.
 *
 * 700ms: long enough that typing a sentence is one request, short enough that
 * a reader who changes a dropdown and looks away sees "Saved" before they do.
 */
const SAVE_DEBOUNCE_MS = 700;
/** How long "Saved" stays up before the line goes quiet again. */
/** How long "Saved" stays before the line goes quiet again. It flashed for
 *  under two seconds and was easy to miss (Zach, LED-529); three is long
 *  enough to see after looking up from the field. */
const SAVED_CLEAR_MS = 3000;

/** What the page is currently saying about the reader's last change. A
 *  failure carries its reason and never clears itself. */
type SaveState =
  | { kind: "idle" }
  | { kind: "dirty" }
  | { kind: "saving" }
  | { kind: "saved" }
  | { kind: "failed"; message: string };

function Field({
  label,
  value,
  multiline = false,
}: {
  label: string;
  value: string;
  multiline?: boolean;
}) {
  const empty = value === "Not set" || value === "—" || value === "";
  const [state, setState] = useState<"idle" | "done" | "failed">("idle");
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);
  useEffect(() => () => void (timer.current && clearTimeout(timer.current)), []);

  const copy = async () => {
    if (empty) return;
    const ok = await copyToClipboard(value);
    setState(ok ? "done" : "failed");
    if (timer.current) clearTimeout(timer.current);
    timer.current = setTimeout(() => setState("idle"), COPY_CONFIRM_MS);
  };

  const title =
    state === "done"
      ? `Copied ${label}`
      : state === "failed"
        ? "Copy failed"
        : `Copy ${label}`;

  return (
    <div
      className="frow"
      data-testid="detail-field"
      data-copy-state={state}
      // The row copies. `onClick` rather than a wrapping button because the
      // row holds its own control (below) and a button inside a button is not
      // a thing the DOM allows.
      onClick={empty ? undefined : () => void copy()}
      style={{
        position: "relative",
        display: "flex",
        alignItems: multiline ? "flex-start" : "center",
        gap: 16,
        minHeight: 54,
        padding: multiline ? "14px 22px" : "0 22px",
        // Every row rules ITSELF off from the one above. The first row's rule
        // is dropped in CSS (`.frow:first-child`, `main.tsx`) — an inline
        // `borderTop` on row one drew a hairline directly under the card's own
        // top edge, which reads as an empty row sitting above the first field
        // (user-directed, 2026-09-07). `:first-child` is the only way to say
        // "every row but the first" without threading an index through.
        borderTop: `1px solid ${C.hairRow}`,
        cursor: empty ? undefined : "pointer",
        background: state === "done" ? C.greenSoft : undefined,
        transition: "background 0.18s ease",
      }}
    >
      <span
        data-testid="detail-field-label"
        style={{
          flex: "none",
          width: 135,
          fontSize: 15.52,
          fontWeight: 500,
          color: C.sub,
          paddingTop: multiline ? 2 : undefined,
        }}
      >
        {label}
      </span>

      <span
        style={{
          flex: 1,
          minWidth: 0,
          display: "flex",
          alignItems: "center",
          justifyContent: "flex-end",
        }}
      >
        <span
          data-testid="detail-field-value"
          style={{
            fontSize: multiline ? 16.1 : 17.83,
            fontWeight: multiline ? 500 : 700,
            letterSpacing: multiline ? undefined : "-0.3px",
            lineHeight: multiline ? 1.45 : undefined,
            color: empty ? C.sub2 : C.ink,
            textAlign: multiline ? "left" : "right",
            minWidth: 0,
            whiteSpace: multiline ? "pre-wrap" : "normal",
          }}
        >
          {value}
        </span>
      </span>

      {/* LAST in the row, past the value — a copy rail down the right-hand
          edge, the way the reference does it (user-directed, 2026-09-07).
          It used to be `position: absolute; left: 163`, pinned just past the
          label, which put it in the middle of the row between the field's
          name and its value.

          Laid out in normal flow rather than absolutely, so the marks line up
          in a column across rows of different heights without anyone
          maintaining a second set of offsets. An empty row still renders the
          spacer, or the Description's mark would sit further right than the
          rest. */}
      {empty ? (
        <span aria-hidden style={{ flex: "none", width: 28 }} />
      ) : (
        <button
          className="kb cp"
          type="button"
          data-testid="copy-button"
          data-copy-field={label.toLowerCase()}
          data-copy-state={state}
          aria-label={title}
          title={title}
          onClick={(event) => {
            // The row is already listening. Without this the click would copy
            // twice and restart the confirmation mid-flight.
            event.stopPropagation();
            void copy();
          }}
          style={{
            flex: "none",
            alignSelf: multiline ? "flex-start" : "center",
            marginTop: multiline ? 2 : undefined,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            width: 28,
            height: 28,
            padding: 0,
            border: "none",
            borderRadius: 8,
            background: state === "done" ? "transparent" : C.faint,
            color: state === "done" ? C.green : state === "failed" ? C.red : C.ink,
            cursor: "pointer",
          }}
        >
          <CopyMark state={state} />
        </button>
      )}
    </div>
  );
}

/** How long the tick stays up: long enough to read, short enough that the row
 *  is ready again before the reader's next field. */
const COPY_CONFIRM_MS = 1400;

/** Two sheets of paper, and the tick that says the write landed — the design's
 *  own two states, at the same weight as the row's other marks. */
function CopyMark({ state }: { state: "idle" | "done" | "failed" }) {
  if (state === "done") {
    return (
      <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor"
        strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
        <path d="M5 13l4 4L19 7" />
      </svg>
    );
  }
  return (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor"
      strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
      <rect x="9" y="9" width="11" height="11" rx="2.4" />
      <path d="M15 5.5H6a1.5 1.5 0 0 0-1.5 1.5v9" />
    </svg>
  );
}

type EditDraft = {
  title: string;
  fieldValues: Record<string, string>;
  references: Partial<Record<"location" | "assignee" | "vendor" | "trade", ItemEditDirectoryOption>>;
};

function withReference(
  references: EditDraft["references"],
  key: keyof EditDraft["references"],
  selected: ItemEditDirectoryOption | undefined,
): EditDraft["references"] {
  const next = { ...references };
  if (selected) next[key] = selected;
  else delete next[key];
  return next;
}

function draftValue(value: ProcoreDailyLogFieldValue | undefined): string {
  if (value === null || value === undefined) return "";
  return String(value);
}

function patchValue(
  field: ProcoreDailyLogFieldDefinition,
  value: string,
): ProcoreDailyLogFieldValue {
  const trimmed = value.trim();
  if (trimmed === "") return null;
  if (field.type === "boolean") return trimmed === "true";
  if (field.type === "number") {
    const parsed = Number(trimmed);
    return Number.isFinite(parsed) ? parsed : null;
  }
  return value;
}

function fieldOptions(
  field: ProcoreDailyLogFieldDefinition,
  isObservation: boolean,
  observationTypes?: string[],
): readonly string[] | null {
  if (field.type === "boolean") return ["true", "false"];
  if (field.key === "type" && isObservation)
    return observationTypes && observationTypes.length > 0
      ? observationTypes
      : DEFAULT_PROCORE_OBSERVATION_TYPE_OPTIONS;
  if (field.key === "priority" && isObservation)
    return OBSERVATION_PRIORITY_OPTIONS;
  if (field.key === "status") {
    return isObservation
      ? OBSERVATION_STATUS_OPTIONS
      : DAILY_LOG_STATUS_OPTIONS;
  }
  if (field.key === "delayType") return PROCORE_DELAY_TYPE_OPTIONS;
  return null;
}

function editorControl({
  field,
  value,
  onChange,
  isObservation,
  observationTypes,
}: {
  field: ProcoreDailyLogFieldDefinition;
  value: string;
  onChange: (value: string) => void;
  isObservation: boolean;
  observationTypes?: string[];
}) {
  const options = fieldOptions(field, isObservation, observationTypes);
  const inputStyle = {
    width: "100%",
    boxSizing: "border-box" as const,
    minHeight: 36,
    padding: "8px 10px",
    borderRadius: 8,
    border: `1px solid ${C.line2}`,
    background: C.surface,
    color: C.ink,
    fontSize: 14.95,
    fontFamily: "inherit",
  };
  if (options) {
    const includesValue = value === "" || options.includes(value);
    return (
      <select
        aria-label={field.label}
        data-testid={`edit-field-${field.key}`}
        value={value}
        onChange={(event) => onChange(event.target.value)}
        style={inputStyle}
      >
        <option value="">Not set</option>
        {!includesValue && <option value={value}>{value}</option>}
        {options.map((option) => (
          <option key={option} value={option}>
            {field.type === "boolean"
              ? option === "true"
                ? "Yes"
                : "No"
              : option}
          </option>
        ))}
      </select>
    );
  }
  if (field.type === "textarea") {
    return (
      <textarea
        aria-label={field.label}
        data-testid={`edit-field-${field.key}`}
        value={value}
        placeholder={field.placeholder}
        onChange={(event) => onChange(event.target.value)}
        style={{
          ...inputStyle,
          minHeight: 92,
          resize: "vertical",
          lineHeight: 1.4,
        }}
      />
    );
  }
  if (field.type === "time") {
    // The platform's own `<input type="time">` renders a spinner wheel that
    // cannot be typed into at all in iOS WKWebView — see `TimeField`'s file
    // doc. `date` and `number` keep the native input; this is time only.
    return (
      <TimeField
        fieldKey={field.key}
        label={field.label}
        value={value}
        onChange={onChange}
        testId={`edit-field-${field.key}`}
      />
    );
  }
  return (
    <input
      aria-label={field.label}
      data-testid={`edit-field-${field.key}`}
      type={field.type === "date" || field.type === "number" ? field.type : "text"}
      value={value}
      placeholder={field.placeholder}
      onChange={(event) => onChange(event.target.value)}
      style={inputStyle}
    />
  );
}

// ---------------------------------------------------------------------------
// The view
// ---------------------------------------------------------------------------

export interface ItemDetailProps extends ConsoleViewProps {
  itemId: string;
  onBack: () => void;
  backHref: string;
}

export function ItemDetail(props: ItemDetailProps) {
  const {
    itemId,
    search,
    refreshKey,
    personalHref,
    onGoPersonal,
    onBack,
    backHref,
    hrefForWalk,
    onOpenWalk,
  } = props;
  const [nudgeOpen, setNudgeOpen] = useState(false);
  const [evidenceViewer, setEvidenceViewer] = useState<number | null>(null);
  const [photoPickerOpen, setPhotoPickerOpen] = useState(false);
  const [photoBusy, setPhotoBusy] = useState(false);
  const [photoError, setPhotoError] = useState<string | null>(null);
  const [linkCopied, setLinkCopied] = useState(false);
  const [editRevision, setEditRevision] = useState(0);
  /** What the page is saying about the last change — see `queueSave`. */
  const [saveState, setSaveState] = useState<SaveState>({ kind: "idle" });
  /** The last patch reported by the field table, waiting for the debounce. */
  const fieldPatch = useRef<Record<string, unknown> | null>(null);
  const dirty = useRef(false);
  /** The title as the reader is typing it — `null` while untouched, so a
   *  refetch of the record is free to change it under them. */
  const [titleDraft, setTitleDraftState] = useState<string | null>(null);
  /** The same draft, for the debounced save. The timer holds the closure of
   *  the keystroke that armed it, whose `titleDraft` is one character behind;
   *  the ref is always the latest. */
  const titleDraftRef = useRef<string | null>(null);
  const setTitleDraft = (next: string | null) => {
    titleDraftRef.current = next;
    setTitleDraftState(next);
  };
  const saveTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const savedTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  useEffect(
    () => () => {
      if (saveTimer.current) clearTimeout(saveTimer.current);
      if (savedTimer.current) clearTimeout(savedTimer.current);
    },
    [],
  );

  const { data, failure, refreshFailure } =
    useResource<ItemDetailResponse>(
      `item:${itemId}${resourceQuery(search, ["workspace", "kind"])}`,
      () => fetchItemDetail(itemId, search),
      refreshKey + editRevision,
    );

  const incomingTitle = data?.item.title;
  const previousTitle = useRef(incomingTitle);
  useEffect(() => {
    const previous = previousTitle.current;
    previousTitle.current = incomingTitle;
    if (data?.item.kind !== "punch" || incomingTitle === undefined) return;
    // Release an acknowledged title draft so later edits on another device
    // can render. Preserve typing that the server has not acknowledged yet.
    if (titleDraftRef.current === incomingTitle || titleDraftRef.current === previous) setTitleDraft(null);
  }, [incomingTitle]);

  if (failure) {
    return (
      <FailurePanel
        failure={failure as ApiError}
        personalHref={personalHref}
        onGoPersonal={onGoPersonal}
      />
    );
  }
  if (!data) return <LoadingPanel shape="item" />;

  const { item, evidence, history } = data;
  const site = data.siteName || item.jobSiteId;
  // Whole days, rounded DOWN, from the `lastMovementAt` instant — the banner,
  // the "Last movement" fact and the timeline's stall row all print it, and
  // none may claim the item is older than it is. `Math.round` over the
  // already-quantized `daysSinceMovement` did: a 5.97-day item read "6 days".
  const days = wholeDaysSince(item.lastMovementAt, item.daysSinceMovement);
  const isLog = item.kind === "log";
  const mobileFields = mobileRecordFields(item);
  /** What the editable table above already shows, lower-cased — so the
   *  read-only facts below it never print the same property twice. */
  const editableFieldLabels = new Set(
    (mobileFields ?? []).map((field) => field.label.toLowerCase()),
  );
  /**
   * An observation, in either of the two shapes it is stored in.
   *
   * The same predicate `matchesSavedView` uses: a Procore-format observation
   * is a daily-log item with `recordType: "observation"`, and a Note-backed
   * one is `kind: "observation"`. The two land in different branches below
   * and must be treated alike.
   */
  const isObservation = item.kind === "observation" || item.recordType === "observation";
  /**
   * Category, Review, Flagged and Last movement — the AUDIT facts.
   *
   * Not printed on an observation (user-directed, 2026-09-10). Each is
   * either a restatement or a number nobody opens the record to read:
   * `Category` on an observation is always the word "observations", which
   * is what the crumb above already calls the page; `Review` is the state
   * the review queue owns and acts on, not something this page can change;
   * `Flagged` and `Last movement` are the same instant in two different
   * spellings on a record nobody has touched since it was raised.
   *
   * They stay on a DAILY LOG, where `Category` is the entry's actual kind —
   * weather, deliveries, manpower — and is the first thing a reader checks.
   */
  // Punch records have the same focused, action-ready detail treatment as
  // observations. Their meaningful properties live in `fieldsFor` below;
  // repeating generated audit facts made Punch detail a visibly different,
  // much noisier page for no reader-facing gain.
  const showAuditFacts = !isObservation && item.kind !== "punch";
  // Keep Punch's specific workflow state (e.g. Verified), not just the
  // normalized list grouping (Closed).
  const statusLabel = item.kind === "punch" && item.workflowStatus?.trim()
    ? humanize(item.workflowStatus.trim())
    : STATUS_CHIP[item.status].label;
  const mobileDefinition = mobileRecordDefinition(item);
  const mobileKind = mobileRecordKindLabel(item);
  /**
   * Whether this record's fields are the editable table or the read-only one.
   *
   * TASKS join the mobile-schema records here, at the user's direction
   * (2026-09-09): *the design should not be different from review to
   * post-review*. `fieldsFor` in `ReviewQueue` has always had a per-kind
   * branch for a task — description, due date, status, assignee, with the
   * `patchKey`s `updateReviewedItem` accepts — so the review card and this
   * page draw the same four rows from the same code, and the only thing that
   * used to differ was that this page refused to let anyone type in them.
   *
   * Punch uses the same shared field table as an observation. `fieldsFor`
   * already defines its Procore-backed description, priority, due date,
   * location, assignee and trade fields, and the edit route accepts the
   * same post-review patch. Keeping it read-only here made the detail page
   * visibly diverge from its review card and from observations.
   */
  const canEdit =
    Boolean(mobileFields && mobileDefinition) ||
    item.kind === "task" ||
    item.kind === "punch";
  const canMessage = !isLog && item.kind !== "punch";
  // Tasks and punches display Procore's human-facing number. Their remoteId
  // is an API resource key; omit the number until sync has captured it.
  // An Autodesk Issue shows ACC's own number — never Procore's fields,
  // which an ACC record does not carry.
  const accIssue = item.syncSource === "autodesk" && item.kind !== "task" && item.kind !== "log"
    ? data.integrationSync?.autodesk
    : undefined;
  const headerNumber =
    item.syncSource === "autodesk"
      ? accIssue?.number ? String(accIssue.number) : undefined
      : item.kind === "punch"
        ? item.punchNumber
        : item.kind === "task"
          ? item.procoreSync?.remoteNumber
          : item.procoreSync?.remoteNumber ?? item.procoreSync?.remoteId;
  const viewerPhotos: FullscreenPhoto[] = evidence.map((photo) => ({
    ...photo,
    // An unprocessed photo must not fall back to the generic image caption.
    description: photo.description ?? "No description",
    siteName: site,
    walkName: item.title,
  }));

  /**
   * Every change saves itself.
   *
   * ## Why there is no Save button
   *
   * There was: Edit, then a form, then Save changes and Cancel. The user's
   * instruction is that every item is simply in an editable state
   * (2026-09-07), which means the write has to happen without a button —
   * otherwise the page would collect edits it never promised to keep.
   *
   * ## Debounced, not per-keystroke
   *
   * `FieldRows` reports the WHOLE patch on every change, so a typed
   * description would be one request per character. The timer coalesces them
   * and the last patch wins, which is also what makes the save idempotent:
   * it is a full patch of the record's fields, not a diff, so a dropped
   * intermediate save cannot leave the record half-applied.
   *
   * ## What it says while it does it
   *
   * "Saving…", then "Saved", then nothing after a beat. A failure STAYS, with
   * the reason, and does not clear itself — the reader's typing is still on
   * screen and still unsaved, and a message that disappeared would leave them
   * believing it landed.
   */
  const queueSave = (patch?: Record<string, unknown>) => {
    if (patch) fieldPatch.current = patch;
    dirty.current = true;
    if (saveTimer.current) clearTimeout(saveTimer.current);
    setSaveState({ kind: "dirty" });
    saveTimer.current = setTimeout(() => void flushSave(), SAVE_DEBOUNCE_MS);
  };

  const flushSave = async () => {
    if (!dirty.current) return;
    dirty.current = false;
    const savedFields = fieldPatch.current;
    // The title and the field table are two controls and ONE patch. Sending
    // them separately would make a reader who renamed a record and changed
    // its due date in the same breath race two writes at one document.
    const patch: Record<string, unknown> = {
      ...(savedFields ?? {}),
      ...(titleDraftRef.current !== null ? { title: titleDraftRef.current } : {}),
    };
    setSaveState({ kind: "saving" });
    const result = await postItemEdit(item.kind, item.id, search, patch);
    if (!result.ok) {
      setSaveState({
        kind: "failed",
        message: result.error ?? "LedAI could not save this change.",
      });
      return;
    }
    // Do not resend an old field patch when a later edit only changes the
    // title: another device may have changed those fields since this save.
    if (fieldPatch.current === savedFields) fieldPatch.current = null;
    setSaveState({ kind: "saved" });
    // The record is re-read so the rest of the page — the history timeline,
    // the sync chip — agrees with the fields the reader just changed.
    setEditRevision((revision) => revision + 1);
    if (savedTimer.current) clearTimeout(savedTimer.current);
    savedTimer.current = setTimeout(() => setSaveState({ kind: "idle" }), SAVED_CLEAR_MS);
  };

  // The record's Procore or ACC sync — the record page's sidebar row and the
  // field-table page's header row both draw it, and both offer Retry sync on
  // a failure. A console observation is a Note, which never syncs.
  const syncLook = recordSyncLook(item, item.syncSource === "autodesk" ? data.integrationSync?.autodesk : undefined);
  const retrySync: RetrySync | undefined = item.kind === "observation" ? undefined : async () => {
    const result = await postItemRetrySync(item.kind, item.id, search);
    // Re-read either way: a refused retry may still have moved the state.
    setEditRevision((revision) => revision + 1);
    return { ok: result.ok, ...(result.error ? { error: result.error } : {}) };
  };

  // Tasks, punch items and observations get the record page (Claude Design
  // "Record Detail Web v2"). Daily-log entries keep the field table below.
  if (usesRecordLayout(item)) {
    const activity: RecordActivityRow[] = buildActivity(history, item, syncLook).map(({ entry: _entry, ...line }) => line);
    // A person named on the `drafted` event first, then the record's owner
    // (whose walk made it — most records have no `drafted` event at all),
    // and "Led AI" only for a system draft no one can be named for.
    const drafted = history.find((entry) => entry.type === "drafted");
    const draftedBy = drafted && drafted.actor && !("system" in drafted.actor)
      ? actorName(drafted)
      : undefined;
    const creator = draftedBy ?? data.creator?.name ?? (drafted ? "Led AI" : undefined);
    const reviewHref = reviewRecordHref(props.hrefForReview(item.jobSiteId), item);
    /**
     * Photo writes — the same routes the review card uses: attach, upload
     * and remove through `/api/items/:kind/:id/media`, and the walk photo's
     * own description and location through `/api/walks/:visitId/photos/…`
     * (the phone's `updatePhotoCaption` and `setWalkPhotoLocation`). Each
     * re-reads the record so the carousel shows what actually landed.
     */
    const acc = item.syncSource === "autodesk" && item.kind !== "task";
    const mediaPath = `/api/items/${encodeURIComponent(item.kind)}/${encodeURIComponent(item.id)}/media`;
    const must = (result: MutationResult, message: string) => {
      if (!result.ok) throw new Error(result.error || message);
    };
    const photoWrite = async (write: () => Promise<void>): Promise<boolean> => {
      if (photoBusy) return false;
      setPhotoBusy(true);
      setPhotoError(null);
      let ok = true;
      try {
        await write();
      } catch (error) {
        ok = false;
        setPhotoError(error instanceof Error ? error.message : "Could not update photos.");
      }
      // Also after a partial failure, so photos that did land show.
      setEditRevision((revision) => revision + 1);
      setPhotoBusy(false);
      return ok;
    };
    const visitId = item.visitId;
    const quietButton: React.CSSProperties = {
      display: "inline-flex",
      alignItems: "center",
      height: 32,
      padding: "0 10px",
      borderRadius: 8,
      border: `1px solid ${C.line}`,
      background: "transparent",
      fontSize: 13,
      fontWeight: 600,
      color: C.sub,
      cursor: "pointer",
      whiteSpace: "nowrap",
      fontFamily: "inherit",
    };
    return (
      <>
        <RecordDetail
          item={item}
          evidence={evidence}
          activity={activity}
          siteName={site}
          search={search}
          headerNumber={headerNumber}
          creator={creator}
          autodesk={item.syncSource === "autodesk" ? data.integrationSync?.autodesk : undefined}
          onRetrySync={retrySync}
          title={titleDraft ?? item.title}
          onTitleChange={(next) => {
            setTitleDraft(next);
            queueSave();
          }}
          onPatchChange={queueSave}
          onCommit={() => void flushSave()}
          saveLine={
            saveState.kind === "idle" ? null : (
              <span
                data-testid="save-state"
                data-state={saveState.kind}
                aria-live="polite"
                style={{
                  ...META,
                  fontSize: 10,
                  letterSpacing: "0.08em",
                  color: saveState.kind === "failed" ? C.red : saveState.kind === "saved" ? C.green : C.sub2,
                  textTransform: saveState.kind === "failed" ? "none" : "uppercase",
                  maxWidth: 360,
                }}
              >
                {saveState.kind === "failed"
                  ? saveState.message
                  : saveState.kind === "saved"
                    ? "Saved"
                    : "Saving…"}
              </span>
            )
          }
          backHref={backHref}
          onBack={onBack}
          walkHref={item.visitId ? hrefForWalk(item.visitId) : undefined}
          onOpenWalk={item.visitId ? () => onOpenWalk(item.visitId!) : undefined}
          onOpenPhoto={setEvidenceViewer}
          photoActions={{
            busy: photoBusy,
            error: photoError,
            // An ACC Issue's photos are captured on the phone: no "+" here,
            // and no per-photo location, which would only repeat the
            // Issue's own location under every photo (nothing stores it).
            ...(acc
              ? {}
              : {
                  onAdd: () => {
                    setPhotoError(null);
                    setPhotoPickerOpen(true);
                  },
                }),
            onRemove: (photoId) =>
              void photoWrite(async () =>
                must(await deleteMutation(`${mediaPath}/${encodeURIComponent(photoId)}`, search), "Could not remove photo."),
              ),
            ...(visitId
              ? {
                  onDescription: (photoId: string, description: string) =>
                    void photoWrite(async () =>
                      must(await updateWalkPhotoDescription(visitId, photoId, search, description), "Could not save the description."),
                    ),
                  ...(acc ? {} : { onLocation: (photoId: string, name: string, reference?: ItemEditDirectoryOption) =>
                    void photoWrite(async () =>
                      must(
                        await updateWalkPhotoLocation(
                          visitId,
                          photoId,
                          search,
                          reference?.remoteId
                            ? { name: reference.name, ...(reference.path ? { path: reference.path } : {}), procoreLocationId: reference.remoteId, freeText: false }
                            : { name, freeText: true },
                        ),
                        "Could not save the photo's location.",
                      ),
                    ) }),
                }
              : {}),
          }}
          headerExtras={
            <>
              {item.reviewStatus === "pending" && (
                <a
                  data-testid="detail-review-item"
                  href={reviewHref}
                  onClick={(event) => {
                    event.preventDefault();
                    props.onNavigate(reviewHref);
                  }}
                  style={{ ...quietButton, color: C.goldInk, background: C.pendingTint, border: "none" }}
                >
                  Review item →
                </a>
              )}
              {canMessage && (
                <button type="button" data-testid="prepare-message" onClick={() => setNudgeOpen(true)} style={quietButton}>
                  Message
                </button>
              )}
            </>
          }
        />
        {nudgeOpen && (
          <NudgeSheet itemId={item.id} search={search} onClose={() => setNudgeOpen(false)} />
        )}
        {photoPickerOpen && (
          <RecordPhotoPicker
            visitId={visitId}
            recordTitle={titleDraft ?? item.title}
            attachedIds={evidence.map((photo) => photo.id)}
            search={search}
            busy={photoBusy}
            error={photoError}
            onClose={() => setPhotoPickerOpen(false)}
            onAttach={async (mediaIds) => {
              const ok = await photoWrite(async () => {
                for (const mediaId of mediaIds) must(await postMutation(mediaPath, search, { mediaId }), "Could not attach photo.");
              });
              if (ok) setPhotoPickerOpen(false);
            }}
            onUpload={async (files) => {
              const ok = await photoWrite(async () => {
                for (const file of files) must(await postMutation(mediaPath, search, await readRecordPhoto(file)), `Could not upload ${file.name}.`);
              });
              if (ok) setPhotoPickerOpen(false);
            }}
          />
        )}
        {evidenceViewer !== null && viewerPhotos[evidenceViewer] && (
          <PhotoFullscreenViewer
            photos={viewerPhotos}
            index={evidenceViewer}
            onStep={setEvidenceViewer}
            onClose={() => setEvidenceViewer(null)}
          />
        )}
      </>
    );
  }

  // Daily-log entries: the same activity the record page shows, sync lines
  // included, in this page's timeline.
  const activity = buildActivity(history, item, syncLook);

  return (
    <>
      {/* ---------- header bar ---------- */}
      {/* 17a's crumb: one mono line, no bordered bar. The rule under the old
          bar drew a second horizontal edge directly above the chip row's own
          content, and the bar's dark ground made the page start with a strip
          of chrome before it said what the record was. */}
      <div
        style={{
          flexShrink: 0,
          display: "flex",
          alignItems: "center",
          gap: 10,
          padding: "26px 28px 0",
          ...META,
          fontSize: 10.93,
          letterSpacing: "0.1em",
          color: C.sub2,
        }}
      >
        <a
          href={backHref}
          data-testid="detail-back"
          onClick={(event) => {
            event.preventDefault();
            onBack();
          }}
          style={{ display: "flex", alignItems: "center", gap: 4, color: C.sub }}
        >
          <svg width="11" height="11" viewBox="0 0 24 24" fill="none" aria-hidden>
            <path
              d="M15 5l-7 7 7 7"
              stroke="currentColor"
              strokeWidth="2.2"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
          Back
        </a>
        <span style={{ width: 1, height: 12, background: C.line2 }} />
        <span style={{ minWidth: 0, overflow: "hidden", textOverflow: "ellipsis" }}>
          {`${site} · Items`}
        </span>
        <span style={{ flex: 1 }} />
      </div>

      <ViewBody wide="record">
        {refreshFailure && <StaleBanner failure={refreshFailure} />}

        {/* ---------- stall banner ---------- */}
        {item.stalled && (
          <div
            data-testid="stall-banner"
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              gap: 24,
              padding: "20px 28px",
              borderRadius: 16,
              background: C.redSoft,
              // A hairline in the banner's own red, not the full stroke. The
              // solid one drew a box that shouted as loudly as the sentence
              // inside it.
              border: `1px solid ${C.redLine}`,
              marginBottom: 28,
            }}
          >
            <span style={{ flex: 1, minWidth: 0, fontSize: 21.85, fontWeight: 800, letterSpacing: "-0.4px" }}>
              {`No movement in ${plural(days, "day", "days")}`}
            </span>
            {canMessage && (
              <button
                onClick={() => setNudgeOpen(true)}
                data-testid="stall-nudge"
                style={{
                  flexShrink: 0,
                  height: 52,
                  padding: "0 24px",
                  borderRadius: 12,
                  background: C.gold,
                  color: C.onGold,
                  fontSize: 17.25,
                  fontWeight: 700,
                  letterSpacing: "-0.2px",
                }}
              >
                Message
              </button>
            )}
          </div>
        )}

        {/* ---------- chips + title ---------- */}
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: 8,
            flexWrap: "wrap",
            // 17a's 12px between the crumb line and this row. The crumb has
            // no bottom padding of its own, so the gap lives here.
            marginTop: 12,
          }}
        >
          {/*
            17a's three: kind, status, priority — and all three ALWAYS.

            They used to be suppressed the moment a record had a mobile
            schema, on the reasoning that the table below already carries
            status and priority. 17a draws them anyway, and it is right: the
            chips answer "what am I looking at" from the top of the page,
            before the eye has reached a table, and the two most common
            questions about an item are how urgent it is and whether it is
            still open. Repeating them costs one line and saves a scroll.

            One chip shape for all three, keyed on tone — the design's own
            5%-ink ground, with gold at 28% for the priority, which is the
            only one of the three that is a judgement.
          */}
          <HeaderChip label={mobileKind ?? KIND_LABEL[item.kind]} />
          <HeaderChip label={statusLabel} />
          {item.severity && SEVERITY[item.severity] && (
            <HeaderChip label={SEVERITY[item.severity]!.label} tone="priority" />
          )}
          {item.reviewStatus === "pending" && (
            <a data-testid="detail-review-item" href={reviewRecordHref(props.hrefForReview(item.jobSiteId), item)}
              onClick={event => { event.preventDefault(); props.onNavigate(reviewRecordHref(props.hrefForReview(item.jobSiteId), item)); }}
              style={{ color: C.goldInk, fontSize: 14.95, fontWeight: 650, padding: "8px 12px", borderRadius: 9, background: C.pendingTint }}>
              Review item →
            </a>
          )}

          <span style={{ flex: 1 }} />

          {/* 17a's identity group: the number people cite, and the link they
              cite it with, in one control. The copy button used to be a
              52px-wide block at the bottom of the Actions card, three
              scrolls from the title it copies a link to — which is not
              where anyone reaches for it. */}
          <span
            data-testid="detail-identity"
            style={{
              display: "flex",
              alignItems: "center",
              gap: 8,
              height: 34,
              padding: headerNumber ? "0 6px 0 12px" : "0 6px",
              borderRadius: 9,
              background: C.surface,
              border: `1px solid ${C.line}`,
              boxShadow: `0 1px 2px ${C.softShadow}`,
              flex: "none",
              whiteSpace: "nowrap",
            }}
          >
            {/* Only when Procore has given it one. A record that has not
                synced HAS no number, and a placeholder would be a claim. */}
            {headerNumber && (
              <>
                <span
                  data-testid="detail-remote-id"
                  style={{
                    fontFamily: FONT_MONO,
                    fontSize: 13.8,
                    fontWeight: 600,
                    letterSpacing: "0.02em",
                  }}
                >
                  {headerNumber}
                </span>
                <span style={{ width: 1, height: 16, background: C.line }} />
              </>
            )}
            <button
              type="button"
              className="kb"
              data-testid="copy-link"
              onClick={async () => {
                try {
                  await navigator?.clipboard?.writeText(location.href);
                  setLinkCopied(true);
                } catch {
                  setLinkCopied(false);
                }
              }}
              style={{
                display: "flex",
                alignItems: "center",
                gap: 6,
                height: 26,
                padding: "0 9px",
                borderRadius: 6,
                border: "none",
                background: linkCopied ? C.greenSoft : C.hairRow,
                color: C.ink,
                fontSize: 14.37,
                fontWeight: 600,
                cursor: "pointer",
              }}
            >
              <svg width="12" height="12" viewBox="0 0 24 24" fill="none" aria-hidden>
                <path
                  d="M9.5 14.5l5-5M8 12H6.5a3.5 3.5 0 1 1 0-7H10M14 5h3.5a3.5 3.5 0 1 1 0 7H16"
                  stroke={C.sub}
                  strokeWidth="1.9"
                  strokeLinecap="round"
                />
              </svg>
              {linkCopied ? "Copied" : "Copy link"}
            </button>
          </span>

          {/* The record page's sync row and card, on a chip: Procore or ACC,
              its state, and on hover when it last moved, the reason a
              failure gave and Retry sync. Absent for a record never pushed
              — `procoreSyncLook` reads an empty sync subdocument as no
              state, and never a Procore stamp on an Autodesk record. */}
          {syncLook && (
            <SyncRow
              look={syncLook}
              onRetry={retrySync}
              align="right"
              // No inline background: it would outrank the row's hover tint.
              triggerStyle={{ height: 34, margin: 0, padding: "0 12px 0 10px", borderRadius: 9, border: `1px solid ${C.hair}`, whiteSpace: "nowrap" }}
            />
          )}
        </div>
        {/* The title is the record's headline AND its title field. It reads
            as the headline until the reader puts a cursor in it — a 40px
            input with a box around it would make the page look like a form
            the moment it loaded, which is the opposite of what "always
            editable" is for. `canEdit` gates it: a record with no mobile
            schema has no title field to write. */}
        {canEdit ? (
          <textarea
            data-testid="detail-title"
            aria-label="Title"
            rows={1}
            value={titleDraft ?? item.title}
            onChange={(event) => {
              setTitleDraft(event.target.value);
              queueSave();
            }}
            onBlur={() => void flushSave()}
            style={{
              display: "block",
              width: "100%",
              maxWidth: 760,
              boxSizing: "border-box",
              margin: "12px 0 22px",
              padding: 0,
              border: "none",
              background: "transparent",
              color: C.ink,
              fontSize: 30,
              fontWeight: 800,
              letterSpacing: "-0.9px",
              lineHeight: 1.15,
              fontFamily: "inherit",
              resize: "none",
              overflow: "hidden",
              // The box grows with the title rather than scrolling inside a
              // one-line window: a record's title is often two lines and the
              // page has always shown all of it.
              height: "auto",
              fieldSizing: "content",
            } as React.CSSProperties}
          />
        ) : (
          <div
            data-testid="detail-title"
            style={{
              fontSize: 30,
              fontWeight: 800,
              letterSpacing: "-0.9px",
              lineHeight: 1.15,
              maxWidth: 900,
              margin: "8px 0 24px",
            }}
          >
            {item.title}
          </div>
        )}

        {/* ---------- two columns ---------- */}
        <div
          style={{
            display: "grid",
            // The design's own split: the evidence takes the room it needs and
            // the record rail is a fixed 520, so a field row's 150px label
            // column means the same thing on every item.
            // FIELDS FIRST, and they are the wider column now.
            //
            // The record's own fields used to sit in a 340–520px right rail
            // with the photos and the history beside them, which put the
            // thing a reader came to READ AND CHANGE in the narrow column and
            // the two things they only glance at in the wide one. Reversed at
            // the user's direction (2026-09-09): the fields lead, and the
            // evidence and the timeline are the rail.
            // 17a: `minmax(0,1fr) minmax(0,1.15fr)`. The rail is the wider
            // of the two now, and deliberately: it holds photographs at full
            // width, where the record column holds a property table whose
            // rows are a label and a short value. Giving the table the extra
            // 15% would stretch "Medium" further from the word Priority; the
            // photographs use every pixel they are given.
            gridTemplateColumns: ITEM_DETAIL_COLUMNS,
            gap: 20,
            alignItems: "start",
          }}
          /* `.dlcols`, the console's own rule for "a fixed track beside a
             flexible one, until there is no room for both". Without it the
             rail keeps its full 420px all the way down and the record column
             is what gives — measured at 51px on a 908px viewport, with the
             card's contents overflowing and the rail painted over them. That
             was true of the field table too; the properties column is simply
             where it became visible. */
          className="dlcols"
        >
          {/* ===== left: the record itself ===== */}
          <div>
            <Card
              style={{ marginBottom: 20, padding: 0, overflow: "visible" }}
              testId="fields-card"
            >
              {/*
                17a's card header: `DETAILS` and, beside it, what the page is
                doing about saving.

                The card used to open straight on its first field, on the
                argument that a label saying "Details" over a list of details
                is a label saying nothing. 17a puts one there anyway, and the
                reason is the second half of the row: `saves as you go` is
                the only thing this page says about writing, and it has to
                live where the writing happens. It was in an Actions card
                below the fold, which on a record with no other action was a
                whole card containing one sentence about saving.

                The line is LIVE. "Saving…", then "Saved", then back to the
                standing promise; a failure stays, in the failure's own
                words, because the reader's change is still on screen and
                still unsaved.
              */}
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 12,
                  padding: "12px 18px 10px",
                }}
              >
                <span style={{ ...META, fontSize: 10.93, letterSpacing: "0.1em", color: C.ink }}>
                  Details
                </span>
                {canEdit && (
                  <span
                    data-testid="save-state"
                    data-state={saveState.kind}
                    aria-live="polite"
                    style={{
                      ...META,
                      fontSize: 10.35,
                      letterSpacing: "0.08em",
                      // Green when it landed, red when it did not: the one
                      // moment this line has news, it should look like news.
                      color: saveState.kind === "failed" ? C.red : saveState.kind === "saved" ? C.green : C.sub2,
                      textTransform: saveState.kind === "failed" ? "none" : "uppercase",
                      minWidth: 0,
                      lineHeight: 1.4,
                      display: "inline-flex",
                      alignItems: "center",
                      gap: 5,
                    }}
                  >
                    {saveState.kind === "saved" && (
                      <svg aria-hidden width="11" height="11" viewBox="0 0 24 24" fill="none" style={{ flexShrink: 0 }}>
                        <path d="m5 12 4 4L19 6" stroke="currentColor" strokeWidth="2.6" strokeLinecap="round" strokeLinejoin="round" />
                      </svg>
                    )}
                    {saveState.kind === "failed"
                      ? saveState.message
                      : saveState.kind === "saving"
                        ? "Saving…"
                        : saveState.kind === "saved"
                          ? "Saved"
                          : "Saves as you go"}
                  </span>
                )}
              </div>
              {/* No inner gutter on the editable path any more. The table
                  used to sit in 12px of padding inside the card, which is
                  what put a second rounded box a few pixels inside the first
                  — and left the read-only facts, which are the same table
                  continued, outside it. One table, one edge. */}
              <div style={{ padding: canEdit ? "0 0 10px" : "6px 0 10px" }}>
                {canEdit ? (
                  /* The REVIEW queue's own field table, always live.

                     There is no edit mode any more, and no Save. The record's
                     fields are the record: a page that showed them read-only
                     until you pressed Edit, and then redrew them as a second,
                     differently-shaped form, was asking the reader to
                     translate between two pictures of one thing — and the two
                     had already drifted (stacked labels and full-width selects
                     here, a label column and inline controls there). This is
                     that one table, mirrored from Review, with the copy
                     buttons this page has always had kept at the right of
                     every row (user-directed, 2026-09-07).

                     Every change saves itself; `saveState` below is where the
                     page says so. */
                  <div
                    // Leaving a field COMMITS it. The debounce is there to
                    // coalesce typing, not to make the reader wait: moving
                    // focus out of a control is the clearest signal they are
                    // done with it. React's `onBlur` is `focusout`, which
                    // bubbles, so one handler covers every control in the
                    // table.
                    onBlur={() => void flushSave()}
                  >
                    {item.kind === "task" ? (
                      /* Turn 13, on tasks: the properties stop being a table.

                         A field table gives every property the same weight and
                         the same box, which is right for a REVIEWER reading six
                         records in a row and wrong for the record's own home,
                         where five of the six are one word each. 13 makes the
                         value the control: a quiet label → value column, no
                         borders until you touch one, a picker with number keys
                         and a filter line, and the description promoted out of
                         the list into the record's own words.

                         Tasks only, for now, at the user's direction. The
                         component itself is kind-agnostic — it picks a control
                         off each field's own shape — so widening it later is a
                         condition on this line, not a rewrite. */
                      <PropertyEditor
                        item={item}
                        search={search}
                        onPatchChange={queueSave}
                        // A pick commits at once. The picker portals out of
                        // this div, so no `focusout` ever reaches the blur
                        // handler above to flush it, and a finished decision
                        // would otherwise sit in the debounce.
                        onCommit={() => void flushSave()}
                      />
                    ) : (
                      <FieldRows
                        item={item}
                        search={search}
                        onPatchChange={queueSave}
                        onCommit={() => void flushSave()}
                        copyable
                        // The table IS this card's content — see `flush`.
                        flush
                      />
                    )}
                  </div>
                ) : null}
                {canEdit && item.kind === "punch" && (
                  <div data-testid="punch-detail-status">
                    <Field label="Status" value={statusLabel} />
                  </div>
                )}
                {!canEdit ? (
                  <>
                    <Field label="Project" value={site} />
                    <Field label="Location" value={item.location?.name ?? "—"} />
                    <Field label="Kind" value={KIND_LABEL[item.kind]} />
                    {showAuditFacts && (
                      <Field label="Category" value={item.category ?? "—"} />
                    )}
                    <Field
                      label="Severity"
                      value={item.severity ? SEVERITY[item.severity]!.label : "—"}
                    />
                    <Field label="Status" value={statusLabel} />
                    {showAuditFacts && (
                      <>
                        <Field label="Review" value={humanize(item.reviewStatus)} />
                        <Field label="Flagged" value={whenStamp(item.createdAt)} />
                        <Field
                          label="Last movement"
                          value={days < 1 ? "Today" : `${plural(days, "day", "days")} ago`}
                        />
                      </>
                    )}
                  </>
                ) : item.kind !== "task" &&
                  item.kind !== "punch" &&
                  (showAuditFacts || !editableFieldLabels.has("location")) ? (
                  /* The facts the editable table does NOT carry.

                     Review's field list is four rows because a reviewer is
                     deciding, not auditing: description, due date, status,
                     assignee. This page is the record's home, and Location,
                     Category, Review state, when it was flagged and when it
                     last moved are the things a reader opens it to check. So
                     they stay, below the fields, read-only — dropping five
                     facts to make two pages identical would be matching a
                     picture at the cost of the page's job. Kind, Project,
                     Severity and Status are already in the crumb and the
                     chips above, so they are not repeated here.

                     NOT on a task. A task's fields are exactly the phone's —
                     description, status, due date, trade, assignee, company —
                     and the user was explicit that only those are shown
                     (2026-09-09). Everything this block would add is either
                     on the phone's screen already or is chrome this page
                     prints elsewhere. */
                  <div data-testid="record-facts">
                    {/* Location only when the editable table above does NOT
                        already carry it. The mobile schema gives an
                        observation its own `location` field, so printing it
                        here as well put the same place on the page twice —
                        once editable, once not, in two different casings. */}
                    {!editableFieldLabels.has("location") && (
                      <Field label="Location" value={item.location?.name ?? "—"} />
                    )}
                    {showAuditFacts && (
                      <>
                        <Field label="Category" value={item.category ?? "—"} />
                        <Field label="Review" value={humanize(item.reviewStatus)} />
                        <Field label="Flagged" value={whenStamp(item.createdAt)} />
                        <Field
                          label="Last movement"
                          value={days < 1 ? "Today" : `${plural(days, "day", "days")} ago`}
                        />
                      </>
                    )}
                  </div>
                ) : null}
              </div>
            </Card>

            {/* 17a: History sits UNDER Details, in the left column.
                It was in the evidence rail, which put "what happened to this
                record" beside the photographs rather than beside the record
                — so a reader checking who changed what had to cross the page
                to a column they were otherwise only glancing at. */}
            <Card
              testId="history-card"
              style={{
                padding: "18px 20px",
                display: "flex",
                flexDirection: "column",
                gap: 12,
              }}
            >
              <div style={{ display: "flex", alignItems: "baseline", gap: 10 }}>
                <span style={{ ...META, fontSize: 10.93, letterSpacing: "0.1em", color: C.ink }}>
                  History
                </span>
                <span style={{ flex: 1 }} />
                <span style={{ ...META, fontSize: 10.35, color: C.sub2, whiteSpace: "nowrap" }}>
                  Newest first
                </span>
              </div>

              {activity.length === 0 ? (
                <div
                  data-testid="history-empty"
                  style={{
                    display: "flex",
                    gap: 12,
                    alignItems: "flex-start",
                    padding: "16px 18px",
                    borderRadius: 12,
                    background: C.canvas,
                    fontSize: 17.25,
                    lineHeight: 1.5,
                    color: C.sub,
                  }}
                >
                  <svg
                    width="16"
                    height="16"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke={C.green}
                    strokeWidth="2.4"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    aria-hidden
                    style={{ flex: "none", marginTop: 3 }}
                  >
                    <path d="M5 13l4 4L19 7" />
                  </svg>
                  {/* Read successfully and genuinely empty — a failure would
                      have said so, in the failure's own words. */}
                  Nothing has happened to this item yet — it has no recorded
                  events.
                </div>
              ) : (
                <>
                  {/* The design's red stall row, and it leads now. It is not
                      an event — it is the CURRENT state ("nothing has
                      happened since the row below") — so it belongs beside
                      the newest event, which is the top of a newest-first
                      list. It was last in the oldest-first list for exactly
                      the same reason. Derived from the same `stalled` verdict
                      the rest of the console uses; written nowhere. */}
                  {item.stalled && (
                    <TimelineEntry
                      testId="history-stall"
                      what="No movement since"
                      when={`${plural(days, "day", "days")} since last movement`}
                      dot={C.red}
                    />
                  )}
                  {/* NEWEST FIRST. `getItemDetail` returns the timeline
                      oldest-first and keeps doing so: that is the log's own
                      order, its tie-break on `_id` only makes sense that way,
                      and its tests pin it. The ORDER ON SCREEN is a
                      presentation choice, so it is made here — reversing a
                      copy, never `history` itself, which the card above and
                      any future reader still share. */}
                  {activity.map((line) => (
                    <TimelineEntry
                      key={line.id}
                      testId="history-entry"
                      what={line.detail ? `${line.sentence} · ${line.detail}` : line.sentence}
                      when={line.entry ? historyWhen(line.entry) : [line.when, "automatic"].filter(Boolean).join(" · ")}
                      dot={line.sync ? SYNC_TONE[line.sync].fg : historyDot(line.entry!)}
                    />
                  ))}
                </>
              )}
            </Card>
          </div>

          {/* ===== right rail: the evidence ===== */}
          <div>
            <Card
              style={{ marginBottom: 20, padding: "26px 28px" }}
              testId="evidence-card"
            >
              {/* 17a's header: the count is IN the label, and the walk the
                  photographs came from is the one action beside it. */}
              <div style={{ display: "flex", alignItems: "baseline", gap: 10, marginBottom: 16 }}>
                <span style={{ ...META, fontSize: 10.93, letterSpacing: "0.1em", color: C.ink }}>
                  {evidence.length > 0 ? `Photos · ${evidence.length}` : "Photos"}
                </span>
                <span style={{ flex: 1 }} />
                {item.visitId && (
                  <a
                    href={hrefForWalk(item.visitId)}
                    data-testid="evidence-open-walk"
                    onClick={(event) => {
                      if (event.metaKey || event.ctrlKey || event.shiftKey) return;
                      event.preventDefault();
                      onOpenWalk(item.visitId!);
                    }}
                    style={{ fontSize: 14.37, fontWeight: 500, color: C.sub, whiteSpace: "nowrap" }}
                  >
                    Open walk
                  </a>
                )}
              </div>
              {evidence.length === 0 ? (
                <QuietLine>
                  No photos or video on this item. The transcript, the speaker
                  and the location are the record.
                </QuietLine>
              ) : (
                /* 17a stacks them at FULL width, one per row. They were a
                   wrapping 306–414px grid, which on this column put two
                   half-width photographs side by side — and a site photo at
                   306px is a thumbnail of a wall. The rail is the wider column
                   now precisely so the evidence can use it.

                   Each keeps its OWN shape rather than the 240px band they
                   were all cropped to. A site photo is as often portrait as
                   landscape — a crack down a column, a full-height door
                   detail — and `cover` showed a horizontal slice out of the
                   middle of those. `height` is what the tile holds until the
                   image reports its dimensions; `maxHeight` stops one tall
                   portrait from taking the whole page. */
                <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
                  {evidence.map((photo) => (
                    <button
                      key={photo.id}
                      type="button"
                      className="kb"
                      data-testid="evidence-item"
                      onClick={() => setEvidenceViewer(evidence.findIndex((candidate) => candidate.id === photo.id))}
                      style={{
                        display: "block",
                        width: "100%",
                        minWidth: 0,
                        padding: 0,
                        border: "none",
                        background: "transparent",
                        textAlign: "left",
                        cursor: "zoom-in",
                      }}
                    >
                      {/* A presigned GET straight from R2 — console media never
                          round-trips `backend/` (spec §7.2). A null url is a
                          tile with no photo, never a broken image. */}
                      <EvidenceTile
                        jobSiteId={item.jobSiteId}
                        url={photo.url ?? photo.thumbnailUrl}
                        fit="natural"
                        height={240}
                        maxHeight={560}
                        stamp={whenStamp(photo.capturedAt)}
                        alt={photo.description ?? ""}
                      />
                      {photo.description && (
                        <span
                          style={{
                            display: "block",
                            marginTop: 8,
                            fontSize: 15.52,
                            color: C.sub,
                            lineHeight: 1.5,
                          }}
                        >
                          {photo.description}
                        </span>
                      )}
                      {!photo.url && !photo.thumbnailUrl && (
                        <span
                          style={{
                            ...META,
                            display: "block",
                            marginTop: 5,
                            fontSize: 10.35,
                            color: C.sub2,
                          }}
                        >
                          Photo storage is not configured here
                        </span>
                      )}
                    </button>
                  ))}
                </div>
              )}
            </Card>

            {/*
              The Actions card exists only when there IS an action.

              It sits in the right rail, UNDER the photographs (user-directed,
              2026-09-20). It was between Details and History in the record
              column, where a full-width gold button split the fields from
              the timeline that explains them.

              It held two things: the save-state line, which 17a puts in the
              Details header where the writing happens, and a Message button
              that a daily log never gets. On an observation — `kind: "log"`,
              so no Message — what was left was a card, a heading, and one
              sentence about saving: a whole surface stating something the
              page already says at the top of the record.

              The `!canEdit` note below is the other thing worth keeping, and
              it only appears on a record this console cannot edit at all.
            */}
            {(canMessage || !canEdit) && (
            <Card testId="actions-card" style={{ padding: 22 }}>
              <Eyebrow>Actions</Eyebrow>
              {/* NO Edit, Save changes or Cancel.

                  Every change on this page saves itself (see `queueSave`), so
                  the three buttons that used to open, commit and abandon an
                  edit mode are gone with the mode (user-directed,
                  2026-09-07). What is left here are the actions that are
                  actually actions: message whoever holds the record, and copy
                  a link to it.

                  The line below is the page telling the truth about the
                  write. A page that saved silently would be indistinguishable
                  from a page that had stopped saving. */}
              {canMessage && (
                <button
                  onClick={() => setNudgeOpen(true)}
                  data-testid="prepare-message"
                  style={{
                    width: "100%",
                    height: 52,
                    borderRadius: 12,
                    background: C.gold,
                    color: C.onGold,
                    fontSize: 17.25,
                    fontWeight: 700,
                    letterSpacing: "-0.2px",
                    marginBottom: 10,
                  }}
                >
                  Message
                </button>
              )}
              {/* Copy link is in the HEADER now (17a), beside the record's
                  number — which is where a reader reaches for it, and where
                  the thing it copies a link to actually is. It was a 52px
                  block at the foot of this card, three scrolls below the
                  title. Two of them would be two controls doing one thing. */}

              {/* The save error lives on the save-state line above, beside
                  the change it refused, rather than in a second place. */}
              {!canEdit && (
                <div
                  style={{
                    display: "flex",
                    alignItems: "flex-start",
                    gap: 7,
                    marginTop: 12,
                    fontSize: 13.23,
                    color: C.sub2,
                    lineHeight: 1.45,
                  }}
                >
                  <svg
                    width="12"
                    height="12"
                    viewBox="0 0 16 16"
                    fill="none"
                    style={{ flexShrink: 0, marginTop: 2 }}
                  >
                    <rect
                      x="3"
                      y="7"
                      width="10"
                      height="7"
                      rx="1.6"
                      stroke={C.sub2}
                      strokeWidth="1.7"
                    />
                    <path
                      d="M5.5 7V5a2.5 2.5 0 015 0v2"
                      stroke={C.sub2}
                      strokeWidth="1.7"
                    />
                  </svg>
                  <span>
                    This item type is not yet available in the shared
                    mobile-schema editor. Use the LedAI app for its current edit
                    controls.
                  </span>
                </div>
              )}
            </Card>
            )}
          </div>
        </div>
      </ViewBody>

      {nudgeOpen && (
        <NudgeSheet
          itemId={item.id}
          search={search}
          onClose={() => setNudgeOpen(false)}
        />
      )}
      {evidenceViewer !== null && viewerPhotos[evidenceViewer] && (
        <PhotoFullscreenViewer
          photos={viewerPhotos}
          index={evidenceViewer}
          onStep={setEvidenceViewer}
          onClose={() => setEvidenceViewer(null)}
        />
      )}
    </>
  );
}
